import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { LoadingButton } from '@mui/lab'

import PageContainer from '@/components/ui/containers/PageContainer'
import { PatientProfileForm } from '@/features/patient/patient-profile-form'
import { DMPConnectVitaleCardDialog, PlatformVitaleCardDialog, useSelectVitaleCardInfo, useVitaleCardDisplayLogic } from '@/widgets/vitale-card'
import { useIntro } from '@/hooks/useIntro'
import { CreatePatientIntro } from '@/utils/onboarding/intro'

import { CGUDialog } from './CGUDialog'
import { useDialogState } from '../model/useDialogState'

type CreatePatientPageProps = {}

export function CreatePatientPage(props: CreatePatientPageProps) {
  useLingui()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { showDMPConnectVitaleCard, showPlatformVitaleCard } = useVitaleCardDisplayLogic()
  const { closeDialog, dialogState, setDialogState } = useDialogState()
  const introEnabled = useIntro(CreatePatientIntro, dialogState !== 'cgu')
  const { vitaleCardInfo, onDMPConnectVitaleCardSelect, onPlatformVitaleCardSelect } = useSelectVitaleCardInfo()

  return (
    <PageContainer>
      <Paper sx={{ mt: '15px', p: '30px' }}>
        <Stack direction="row" justifyContent="end">
          {showDMPConnectVitaleCard && (
            <Button color="secondary" size="small" onClick={() => setDialogState('add-by-vitale-card')} variant="contained" id="carte-vitale">
              <Trans>Add by vitale card</Trans>
            </Button>
          )}
          {showPlatformVitaleCard && (
            <Button color="secondary" size="small" onClick={() => setDialogState('carte-vitale-infos')} variant="contained" id="carte-vitale">
              {t('page.createPatient.addPatientByCarteVitaleButton.label')}
            </Button>
          )}
        </Stack>
        <PatientProfileForm
          action={({ isSubmitting }) => (
            <Stack direction="row" justifyContent="end" spacing={2}>
              <Button variant="neutral" onClick={() => navigate('/patients')} type="button">
                <Trans>Cancel</Trans>
              </Button>
              <LoadingButton id="add-patient-button" disabled={dialogState === 'cgu'} disableElevation loading={isSubmitting} type="submit" variant="contained">
                <Trans>Add</Trans>
              </LoadingButton>
            </Stack>
          )}
          initialValues={vitaleCardInfo}
          intro={introEnabled}
          mode="create"
        />
      </Paper>
      <DMPConnectVitaleCardDialog
        open={dialogState === 'add-by-vitale-card'}
        onClose={closeDialog}
        title={t`Add by vitale card`}
        onSelectPatient={(patient) => {
          onDMPConnectVitaleCardSelect(patient)
          closeDialog()
        }}
      />
      <PlatformVitaleCardDialog onClose={closeDialog} onSelect={onPlatformVitaleCardSelect} open={dialogState === 'carte-vitale-infos'} />
      <CGUDialog open={dialogState === 'cgu'} onClose={closeDialog} />
    </PageContainer>
  )
}

export default CreatePatientPage
